import React, { useCallback } from "react";
import {
	DndContext,
	closestCenter,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
	DragEndEvent,
} from "@dnd-kit/core";
import {
	SortableContext,
	sortableKeyboardCoordinates,
	verticalListSortingStrategy,
	useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { KeyPersonnelTable } from "./KeyPersonnelTable";
import type { FilterOption } from "newui/components/Filters/TableColumnFilter";
import type {
	KeyPersonnelDetail,
} from "../types/KeyPersonnel.type";

interface SortableKeyPersonnelListProps {
	personnel: KeyPersonnelDetail[];
	profileTypeId?: number;
	sectorFilter?: number[];
	sectorOptions?: FilterOption<number>[];
	onDelete: (id: number) => void;
	onEdit: (id: number) => void;
	/** PRO-709 — hides the row-level edit/delete actions for view-only standing. */
	canEdit?: boolean;
	onReorder: (id: number, newSortOrder: number) => void;
	onSectorFilterChange?: (ids: number[]) => void;
}

interface SortableRowProps {
	cells: React.ReactNode;
	person: KeyPersonnelDetail;
	onEdit: (id: number) => void;
}

const SortableRow: React.FC<SortableRowProps> = ({ person, cells, onEdit }) => {
	const {
		attributes,
		listeners,
		setNodeRef,
		transform,
		transition,
		isDragging,
	} = useSortable({ id: person.id });

	const style: React.CSSProperties = {
		transform: CSS.Transform.toString(transform),
		transition,
		opacity: isDragging ? 0.5 : 1,
	};

	return (
		<tr
			ref={setNodeRef}
			style={style}
			{...attributes}
			className="cursor-pointer hover:bg-gray-50"
			onClick={() => onEdit(person.id)}
			data-testid={`sortable-row-${person.id}`}
		>
			<td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
				<button
					type="button"
					className="cursor-grab text-gray-400 hover:text-gray-600"
					data-testid={`drag-handle-${person.id}`}
					{...listeners}
				>
					&#x2630;
				</button>
			</td>
			{cells}
		</tr>
	);
};

const SortableKeyPersonnelList: React.FC<SortableKeyPersonnelListProps> = ({
	personnel,
	profileTypeId,
	sectorOptions,
	sectorFilter,
	onSectorFilterChange,
	onEdit,
	onDelete,
	canEdit = true,
	onReorder,
}) => {
	const sensors = useSensors(
		useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		}),
	);

	/*
	 * PRO-709: reordering persists via `POST /key-personnel/{id}/reorder`, which
	 * the API now gates on `company_profile,edit`. Dropping a row as a view-only
	 * user would otherwise reorder the list optimistically and then 403, leaving
	 * the UI disagreeing with the server until refetch.
	 */
	const handleDragEnd = useCallback(
		(event: DragEndEvent) => {
			if (!canEdit) {
				return;
			}

			const { active, over } = event;
			if (!over || active.id === over.id) {
				return;
			}

			const oldIndex = personnel.findIndex(
				(p) => p.id === Number(active.id),
			);
			const newIndex = personnel.findIndex(
				(p) => p.id === Number(over.id),
			);

			if (oldIndex === -1 || newIndex === -1) {
				return;
			}

			const movedId = Number(active.id);
			// newIndex + 1 = 1-based sort_order position
			const newSortOrder = newIndex + 1;

			onReorder(movedId, newSortOrder);
		},
		[personnel, onReorder, canEdit],
	);

	const sortableIds = personnel.map((p) => p.id);

	const renderSortableRow = useCallback(
		(person: KeyPersonnelDetail, cells: React.ReactNode) => (
			<SortableRow key={person.id} person={person} cells={cells} onEdit={onEdit} />
		),
		[onEdit],
	);

	return (
		<DndContext
			sensors={sensors}
			collisionDetection={closestCenter}
			onDragEnd={handleDragEnd}
		>
			<SortableContext
				items={sortableIds}
				strategy={verticalListSortingStrategy}
			>
				<KeyPersonnelTable
					personnel={personnel}
					profileTypeId={profileTypeId}
					onEdit={onEdit}
					canEdit={canEdit}
					onDelete={onDelete}
					showDragColumn={canEdit}
					renderRow={renderSortableRow}
					forceShowSector={true}
					sectorOptions={sectorOptions}
					sectorFilter={sectorFilter}
					onSectorFilterChange={onSectorFilterChange}
				/>
			</SortableContext>
		</DndContext>
	);
};

export { SortableKeyPersonnelList };
export type { SortableKeyPersonnelListProps };
